/**
 * 🧱 Refactor BT Phase 3 — section « SectionDescriptionPlanifCreer » de CreerBonTravailDialog.jsx : JSX déplacé TEL QUEL (ex-L509-789, dédenté de 10) ;
 * variables via `useBTForm()` (BTFormProvider enrichi par `extra`). DOM, data-testid, classes, textes inchangés.
 */
import { Button } from '@/components/ui/button';
import { Calendar, Clock, Plus, User, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { EditorRiche } from '@/components/crm/EditorRiche';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PlanificationApproxSection } from '../../PlanificationApproxSection';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { calculerCoutEquipe, calculerHeuresBudgetees } from '../../utils';
import { useBTForm } from '../../BTFormContext';

export function SectionDescriptionPlanifCreer() {
  const {
    addEmployeAssigne,
    dossier,
    employees,
    form,
    handleChefChange,
    isExt,
    isProprietaire,
    notesSoumission,
    planificationApprox,
    removeEmployeAssigne,
    setForm,
    setHeureDebutDirty,
    setPlanificationApprox,
    setShowAddEmploye,
    showAddEmploye,
    type,
  } = useBTForm();
  return (
    <>
      {/* Description */}
      <Card>
        <CardHeader><CardTitle className="text-base">Description des Travaux</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Description (copiée de la soumission)</Label>
            {/* 🆕 2026-05-08 — Éditeur de texte riche TipTap */}
            <div className="mt-1">
              <EditorRiche
                value={form.description_travaux || ''}
                onChange={(html) => setForm(f => ({ ...f, description_travaux: html }))}
                minHeight="160px"
              />
            </div>
          </div>
          <div>
            <Label>Notes spéciales{notesSoumission && <span className="ml-1 text-xs text-blue-500 font-normal">(importées de la soumission)</span>}</Label>
            <div className="mt-1">
              <EditorRiche
                value={form.notes_speciales || ''}
                onChange={(html) => setForm(f => ({ ...f, notes_speciales: html }))}
                minHeight="120px"
              />
            </div>
          </div>
          {/* Notes importantes — visibles dans le Dashboard Production à venir */}
          <div className="border-t pt-3">
            <Label className="flex items-center gap-1.5 text-amber-700 font-semibold">
              <span>⚠</span> Notes importantes (visibles dans Vue d'ensemble)
            </Label>
            <p className="text-xs text-slate-400 mb-1.5">Ex: Apporter la nacelle la veille, Greg part avec la grosse remorque...</p>
            <textarea
              value={form.notes_importantes}
              onChange={(e) => setForm(f => ({ ...f, notes_importantes: e.target.value }))}
              rows={2}
              placeholder="Ces notes apparaîtront dans le tableau Production à venir du Dashboard avec le nom du client..."
              className="w-full border border-amber-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 bg-amber-50/30"
              data-testid="notes-importantes-creer-bt"
            />
            {form.notes_importantes && (
              <div className="mt-2">
                <label className="text-xs text-slate-600 font-medium">Quand s'applique cette note ?</label>
                <select
                  value={form.notes_importantes_timing}
                  onChange={(e) => setForm(f => ({ ...f, notes_importantes_timing: e.target.value }))}
                  className="w-full mt-1 border border-amber-300 rounded-md px-3 py-2 text-sm bg-amber-50/20 focus:outline-none focus:ring-2 focus:ring-amber-300"
                >
                  <option value="avant_debut">Avant le début des travaux (veille de la 1ère journée)</option>
                  <option value="debut_travaux">Début des travaux (1ère journée)</option>
                  <option value="pendant">Pendant les travaux</option>
                  <option value="fin_travaux">Fin des travaux (dernière journée)</option>
                  <option value="apres_fin">Après les travaux</option>
                </select>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Heures budgétées {!isProprietaire && <span className="text-xs text-gray-400">(automatique)</span>}</Label>
              <Input type="number" value={form.heures_budgetees} onChange={(e) => isProprietaire && setForm(f => ({ ...f, heures_budgetees: e.target.value }))} placeholder="0" data-testid="bon-heures" disabled={!isProprietaire} className={!isProprietaire ? 'bg-gray-100' : ''} />
              {/* Détails du calcul auto + estimation jours */}
              {(() => {
                const montant = parseFloat(dossier?.montant_soumission || dossier?.montant_final || 0) || (() => {
                  const s = (dossier?.soumissions || []).find(s => s.statut === 'acceptee');
                  return parseFloat(s?.montant_final || s?.montant_total || 0);
                })();
                const equipe = calculerCoutEquipe(form.chef_equipe_id, employees, form.dates_chantier?.[0]);
                // Utiliser le nombre réel d'employés assignés, pas toute l'équipe du chef
                const nbMembres = form.employes_assignes.length > 0 ? form.employes_assignes.length : equipe.nbMembres;
                // Recalculer le coût moyen des assignés réels
                let coutMoyen = equipe.coutMoyen;
                if (form.employes_assignes.length > 0) {
                  let ct = 0;
                  for (const a of form.employes_assignes) {
                    const emp = employees.find(e => e.id === a.employee_id);
                    ct += parseFloat(emp?.salaires?.cout_employeur || 0);
                  }
                  coutMoyen = ct / form.employes_assignes.length;
                }
                
                if (montant > 0 && coutMoyen > 0) {
                  const calc = calculerHeuresBudgetees(montant, coutMoyen, nbMembres, form.dates_chantier);
                  return (
                    <div className="mt-1.5 space-y-1" data-testid="heures-proposees">
                      {isProprietaire ? (
                        <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-1">
                          {calc.montantHT?.toLocaleString('fr-CA',{style:'currency',currency:'CAD'})} (sans taxes) x 45% / {calc.coutMoyen?.toFixed(2)}$/h = <strong>{calc.heures}h totales</strong>
                        </p>
                      ) : (
                        <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-1">
                          Heures budgétées : <strong>{calc.heures}h totales</strong>
                        </p>
                      )}
                      {calc.heures > 0 && nbMembres > 0 && (
                        <p className="text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1.5 flex items-center gap-1" data-testid="estimation-jours">
                          <Calendar className="w-3.5 h-3.5 shrink-0" />
                          Job à faire en ~{calc.jours} jour(s) ({nbMembres} employé{nbMembres > 1 ? 's' : ''})
                          <span className="text-blue-500 font-normal ml-1">({calc.heures}h totales / {calc.heuresParJour}h×{nbMembres} — saison {calc.saison})</span>
                        </p>
                      )}
                    </div>
                  );
                }
                if (montant <= 0 && form.chef_equipe_id) return <p className="text-xs text-amber-600 mt-1">Montant du contrat manquant</p>;
                return null;
              })()}
            </div>
            <div>
              <Label>Chef d'équipe / Responsable</Label>
              <div className="flex gap-2 items-center">
                <Select value={form.chef_equipe_id || undefined} onValueChange={handleChefChange}>
                  <SelectTrigger className="flex-1"><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                  <SelectContent>
                    {/* 🐛 [Phase v13.46 — 2026-07-14] Sujet 1 : option « — Sans chef — » explicite (sentinelle __none__).
                        Le dropdown ne liste QUE les chefs d'équipe (+ proprios) — plus de « Autres employés ». */}
                    <SelectItem value="__none__" data-testid="chef-option-sans-chef">— Sans chef —</SelectItem>
                    {(() => {
                      // Cat H — `e.role` = rôle DE L'EMPLOYÉ dans la liste (donnée), pas RBAC utilisateur. Filtre pour couronne "propriétaire" dans le dropdown.
                      const isProprio = (e) => e.est_proprietaire === true || e.role === 'proprietaire';  // E3b-whitelist : drapeau est_proprietaire de la fiche (donnée)
                      const isChef = (e) => e.type_employe === 'chef_equipe' || e.role_terrain === 'chef_equipe' || isProprio(e);
                      const isVisible = (e) => e.est_actif !== false || isProprio(e);
                      const chefs = employees.filter(e => isVisible(e) && isChef(e));
                      if (chefs.length === 0) {
                        return (
                          <div className="px-3 py-2 text-xs text-slate-400 italic">Aucun chef d'équipe disponible</div>
                        );
                      }
                      return chefs.map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.prenom} {emp.nom}{isProprio(emp) ? ' 👑' : ''}</SelectItem>
                      ));
                    })()}
                  </SelectContent>
                </Select>
                {form.chef_equipe_id && (
                  <Button type="button" variant="ghost" size="sm" className="text-red-400 hover:text-red-600 px-2" onClick={() => setForm(f => ({ ...f, chef_equipe_id: '', chef_equipe_nom: '', employes_assignes: (f.employes_assignes || []).filter(e => !e.est_chef) }))} title="Retirer le chef d'équipe">
                    <X className="w-4 h-4" />
                  </Button>
                )}
                {/* 🐛 [Phase v13.46 — 2026-07-14] Sujet 1 : badge ambre « ⚠️ Chef à assigner »
                    affiché quand aucun chef n'est sélectionné. */}
                {!form.chef_equipe_id && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-300"
                    data-testid="badge-chef-a-assigner"
                    title="Un chef d'équipe est fortement recommandé pour ce bon de travail"
                  >
                    ⚠️ Chef à assigner
                  </span>
                )}
              </div>
              {/* 🗑️ [Phase 51 cleanup 2026-06-08] CollisionChecker retiré du formulaire BT.
                  L'utilisatrice a explicitement spécifié que les conflits d'horaire
                  doivent être gérés UNIQUEMENT depuis le calendrier de production
                  (badges Position Phase 51 BIS A). */}
              {/* 🧱 Phase 4 — Lot E (équipe fixe abolie) : plus de « membres auto-ajoutés » ; l'équipe se choisit dans la matrice. */}
              {form.chef_equipe_id && (
                <p className="text-xs text-slate-400 mt-1 italic" data-testid="creer-bt-equipe-info">L'équipe du jour se compose dans « Présence par jour » ci-dessous.</p>
              )}
            </div>
          </div>

          {/* Équipe assignée */}
          <div>
            <Label className="flex items-center gap-1"><User className="w-3.5 h-3.5" /> Équipe assignée</Label>
            <div className="mt-2 space-y-1.5">
              {form.employes_assignes.map(emp => (
                <div key={emp.employee_id} className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded px-3 py-1.5">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${emp.est_chef ? 'bg-orange-500' : 'bg-blue-500'}`} />
                    <span className="text-sm font-medium">{emp.employee_nom}</span>
                    {emp.est_chef && <span className="text-xs text-orange-600 bg-orange-100 px-1.5 rounded">Chef</span>}
                  </div>
                  {!emp.est_chef && (
                    <button onClick={() => removeEmployeAssigne(emp.employee_id)} className="text-gray-400 hover:text-red-500 transition-colors">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
              {showAddEmploye ? (
                <Select onValueChange={addEmployeAssigne}>
                  <SelectTrigger className="h-8 text-xs border-dashed" data-testid="add-employe-select"><SelectValue placeholder="Choisir un employé..." /></SelectTrigger>
                  <SelectContent>
                    {employees.filter(e => e.est_actif && !form.employes_assignes.find(a => a.employee_id === e.id)).map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.prenom} {emp.nom}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <button onClick={() => setShowAddEmploye(true)} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 border border-dashed border-blue-300 rounded px-2 py-1 w-full justify-center" data-testid="btn-add-employe-assigne">
                  <Plus className="w-3 h-3" /> Ajouter un employé (toutes équipes)
                </button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Heure de début prévue</Label>
              <Input
                type="time"
                value={form.heure_debut}
                onChange={(e) => {
                  setHeureDebutDirty(true); // 🆕 Phase C4 : user a saisi → respecter sa valeur
                  setForm(f => ({ ...f, heure_debut: e.target.value }));
                }}
                placeholder="07:00"
                data-testid="bon-heure-debut"
              />
              <p className="text-xs text-gray-400 mt-1">Déclenche une alerte SMS si non pointé après 31 min</p>
            </div>
              {/* 🔄 B4 — Checkbox Dates fixes (Eliot, 2026-05-04) */}
              <div className="flex items-start gap-2 pt-6">
                <Checkbox
                  id="dates_fixes"
                  checked={!!form.dates_fixes}
                  onCheckedChange={(v) => setForm(f => ({ ...f, dates_fixes: !!v }))}
                  data-testid="bon-dates-fixes"
                />
                <div className="flex-1">
                  <label htmlFor="dates_fixes" className="text-sm font-medium cursor-pointer flex items-center gap-1">
                    <span className="text-lg leading-none">⛔</span> Dates fixes
                  </label>
                  <p className="text-xs text-gray-400">
                    Ce BT ne peut pas être reporté. Affiché avec ⛔ dans le Calendrier de Production.
                  </p>
                </div>
              </div>
              {!isExt && (
              <div className="flex items-start gap-2 pt-6">
                <Checkbox
                  id="jours_pluie"
                  checked={form.jours_pluie}
                  onCheckedChange={(v) => setForm(f => ({ ...f, jours_pluie: !!v }))}
                  data-testid="bon-jours-pluie"
                />
                <div className="flex-1">
                  <label htmlFor="jours_pluie" className="text-sm font-medium cursor-pointer">Réalisable par temps de pluie</label>
                  <p className="text-xs text-gray-400">Affiche sur le calendrier de production pour assignation rapide</p>
                  {form.jours_pluie && (
                    <div className="mt-2 flex items-center gap-2">
                      <div>
                        <Label className="text-xs text-gray-500">Date début</Label>
                        <Input type="date" value={form.pluie_date_debut} onChange={(e) => setForm(f => ({ ...f, pluie_date_debut: e.target.value }))} className="h-8 text-sm mt-0.5" data-testid="pluie-date-debut" />
                      </div>
                      <span className="text-gray-400 mt-4">→</span>
                      <div>
                        <Label className="text-xs text-gray-500">Date fin</Label>
                        <Input type="date" value={form.pluie_date_fin} onChange={(e) => setForm(f => ({ ...f, pluie_date_fin: e.target.value }))} className="h-8 text-sm mt-0.5" data-testid="pluie-date-fin" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* 🐛 [Phase v13.48 — 2026-07-14] Section Planification approximative (Sujet 4.B).
          Se place AU-DESSUS des dates officielles. La persistance est déclenchée
          par `doSave()` après la création du BT. */}
      <PlanificationApproxSection
        bonId={null}
        planificationApprox={planificationApprox}
        onChange={setPlanificationApprox}
        datesChantierOfficielles={form.dates_chantier}
        employees={employees}
      />

    </>
  );
}
